import { Megaphone } from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { usePublicSite } from "@/context/PublicSiteContext";
import { renderMarkdown } from "@/lib/markdown";
import Seo from "@/components/Seo";
import { siteOgImage, siteUrl } from "@/lib/siteMeta";

/**
 * Announcements feed. Sorted newest-first by the RPC; this page just
 * renders the payload.  Empty state is friendly rather than blank.
 */
export default function SiteAnnouncements() {
  const { orgSlug } = useParams();
  const { site } = usePublicSite();
  if (!site) return null;

  const anns = site.announcements || [];
  const siteName = site.org.display_name || site.org.name;

  return (
    <div data-testid="public-announcements" className="mx-auto max-w-3xl">
      <Seo
        title={`Announcements · ${siteName}`}
        description={`Latest news, closures, and family events from ${siteName}.`}
        canonical={siteUrl(orgSlug, "announcements")}
        ogImage={siteOgImage(orgSlug, "announcements")}
        ogType="website"
        themeColor={site.org.brand_primary_hex}
        siteName={siteName}
      />
      <header className="mb-8">
        <p
          className="text-[10px] font-bold uppercase tracking-[0.18em]"
          style={{ color: "var(--color-primary)" }}
        >
          Announcements
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
          News from {site.org.display_name || site.org.name}
        </h1>
      </header>

      {anns.length === 0 ? (
        <div
          data-testid="public-announcements-empty"
          className="rounded-2xl border border-dashed border-neutral-300 bg-white px-6 py-14 text-center"
        >
          <Megaphone size={28} weight="duotone" className="mx-auto text-neutral-400" />
          <p className="mt-3 font-heading text-base font-semibold text-neutral-800">
            Nothing posted yet
          </p>
          <p className="mt-1 text-sm text-neutral-500">
            Check back soon for updates, closures, and family events.
          </p>
        </div>
      ) : (
        <ul className="space-y-5">
          {anns.map((a) => (
            <li
              key={a.id}
              data-testid="public-announcement-item"
              className="rounded-2xl border border-neutral-200 bg-white p-6"
            >
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">
                {a.published_at
                  ? new Date(a.published_at).toLocaleDateString(undefined, {
                      year: "numeric", month: "short", day: "numeric",
                    })
                  : ""}
              </p>
              <h3 className="mt-1 font-heading text-xl font-semibold text-neutral-900">
                {a.title}
              </h3>
              <div
                className="prose prose-neutral mt-3 max-w-none prose-p:leading-relaxed prose-a:text-[color:var(--color-primary)]"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(a.body_md) }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
